import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useParams } from 'react-router-dom';
import FiltersDropdown from 'feature/dispatching/components/FiltersDropdown';
import FilterChips, { FilterCountChip } from 'feature/dispatching/components/FilterChips';
import { useFilterCount, useUserRolePermissions } from 'hooks';
import { useViewDriverSummaryByDriverQuery } from '../driverStatusEndpoints';

const showV2Features = process.env.REACT_APP_DISPATCHING_VERSION >= '2.0.0';

const boxStyles = {
  backgroundColor: 'surface.light',
  display: 'grid',
  gridTemplateAreas: `
  "t c"
  "s c"
  "f f"`,
  gridTemplateColumns: '1fr 260px',
  p: 3,
  position: 'sticky',
  top: 48,
  zIndex: 2,
};

const DriverHeader = ({
  cardsFilters,
  defaultFiltersState,
  filtersPanel,
  onCreate,
  onRemoveFilter,
}) => {
  const { isDispatchingManage } = useUserRolePermissions();
  const filtersRef = useRef();
  const [filtersOpened, setFiltersOpen] = useState(false);
  const { viewById: viewByDriverId } = useParams();

  const openFilters = R.compose(setFiltersOpen, R.T);
  const closeFilters = R.compose(setFiltersOpen, R.F);

  // prettier-ignore
  const itemFilter = R.compose(
    R.equals(viewByDriverId),
    R.prop('driverId'),
  );
  const { driver = {}, isSuccess } = useViewDriverSummaryByDriverQuery(undefined, {
    selectFromResult: result => ({
      driver: result.data?.find(itemFilter),
      isLoading: result.isLoading,
      isSuccess: result.isSuccess,
    }),
    skip: R.isNil(viewByDriverId),
  });
  const { badgeIds = [] } = driver;

  const { filterCount } = useFilterCount(cardsFilters, defaultFiltersState);

  // prettier-ignore
  const title = isSuccess 
    ? `${R.toUpper(driver.lastName)}, ${R.toUpper(driver.firstName)}` 
    : 'Loading...';

  // prettier-ignore
  const filtersIcon = filtersOpened
    ? <ExpandLessRoundedIcon position="right" variant="buttonsChild" />
    : <ExpandMoreRoundedIcon position="right" variant="buttonsChild" />

  // prettier-ignore
  // renderBadges :: [String] -> String
  const renderBadges = R.compose(
    R.when(
      xs => R.gt(R.length(xs), 20), 
      R.o(R.concat(R.__, '...'), R.take(17))
    ),
    R.join(', ')
  );

  return (
    <Box sx={boxStyles}>
      <Typography sx={{ gridArea: 't' }} variant="h5">
        {title}
      </Typography>
      <Typography sx={{ gridArea: 's' }} variant="body2">
        {renderBadges(badgeIds)}
      </Typography>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 4, gridArea: 'c' }}>
        <Button
          disabled={!isSuccess}
          onClick={openFilters}
          onClose={closeFilters}
          ref={filtersRef}
          variant="text"
        >
          Filters
          {!!filterCount && <FilterCountChip count={filterCount} />}
          {filtersIcon}
        </Button>
        {showV2Features && isDispatchingManage && (
          <Button disabled={!isSuccess} onClick={onCreate}>
            New Driver
          </Button>
        )}
      </Box>
      <Box sx={{ gridArea: 'f' }}>
        <FilterChips
          defaultState={defaultFiltersState}
          filters={cardsFilters}
          onRemove={onRemoveFilter}
        />
      </Box>
      <FiltersDropdown anchorEl={filtersRef.current} onClose={closeFilters} open={filtersOpened}>
        {filtersPanel}
      </FiltersDropdown>
    </Box>
  );
};

DriverHeader.propTypes = {
  cardsFilters: PropTypes.shape({}).isRequired,
  defaultFiltersState: PropTypes.shape({}).isRequired,
  filtersPanel: PropTypes.element.isRequired,
  onCreate: PropTypes.func.isRequired,
  onRemoveFilter: PropTypes.func.isRequired,
};

export default DriverHeader;
